import { produce } from 'immer';
import { isEmpty } from 'lodash';

import type { RootState, SelectorCreator, Slicer } from '@swf/core/store';
import type { GamesByClubs, HomeGamesError, PageData } from '@swf/core/types/Poker';

export default (slice: Slicer<PageData>, createSelector: SelectorCreator) => ({
  get() {
    return slice;
  },
  clubs() {
    return slice((state) => state.clubs);
  },
  tournaments() {
    return slice((state) => state.games.tourns);
  },
  tables() {
    return slice((state) => state.games.tables);
  },
  recentTourns() {
    return slice((state) => state.games.recentTourns);
  },
  registeredTourns() {
    return slice((state) => state.games.registeredTourns);
  },
  playerTournamentResults() {
    return slice((state) => state.playerTournamentResults);
  },
  selectedGames() {
    return slice((state) => state.games.selectedGameIds);
  },
  loading() {
    return slice((state) => state.loading);
  },
  playersByClubId() {
    return slice((state) => state.playersByClubId);
  },
  clubMembers() {
    return slice((state) => state.clubMembers);
  },
  clubSeasons() {
    return createSelector(
      slice,
      (_: RootState, clubId: string) => clubId,
      ({ clubSeasonsById }, clubId) =>
        produce(clubSeasonsById[clubId], (draft = []) => {
          draft.sort((a, b) =>
            a.timeFrom && b.timeFrom
              ? new Date(b.timeFrom).getTime() - new Date(a.timeFrom).getTime()
              : 0
          );
        })
    );
  },
  error() {
    return createSelector(
      slice,
      (_: RootState, level: keyof HomeGamesError) => level,
      ({ error }, level) => error[level]
    );
  },
  clubSeasonStats() {
    return createSelector(
      slice,
      (_: RootState, clubId: string) => clubId,
      ({ clubSeasonStatsById }, clubId) => clubSeasonStatsById[clubId]
    );
  },
  clubSummaries() {
    return slice((state) => state.clubRecentTournamentSummary);
  },
  clubTournamentSummary() {
    return createSelector(
      slice,
      (_: RootState, tournId: number) => tournId,
      ({ clubRecentTournamentSummary }, tournId) =>
        clubRecentTournamentSummary.find((summary) => Number(summary.tournId) === tournId)
    );
  },
  clubSummariesByDate() {
    return slice((state) =>
      state.clubRecentTournamentSummary.reduce((acc, summary) => {
        const { started } = summary;
        const date = started?.split('T')[0];

        if (date) {
          if (!acc[date]) {
            acc[date] = [];
          }

          acc[date].push(summary);
        }

        return acc;
      }, {})
    );
  },
  clubOptionsById() {
    return slice((state) => state.clubOptionsById);
  },
  getScheduledTournTiles() {
    return createSelector(
      slice,
      (rootState: RootState, tournaments: GamesByClubs) => tournaments,
      ({ games: { registeredTourns } }, tournaments) =>
        produce(tournaments, (draft) => {
          const noGames = isEmpty(tournaments);
          const noRegistered = isEmpty(registeredTourns);

          if (noRegistered && !noGames) return tournaments;
          if (noRegistered && noGames) return {};

          registeredTourns.forEach(({ clubId, tournId }) => {
            const index = draft[Number(clubId)]?.findIndex(
              (tourn) => Number(tourn.id) === Number(tournId)
            );
            if (index > -1) {
              draft[Number(clubId)] = draft[Number(clubId)].filter((_, i) => i !== index);
            }
          });
        })
    );
  },
  getRegisteredTournTiles() {
    return createSelector(
      slice,
      (rootState: RootState, tournaments: GamesByClubs) => tournaments,
      ({ games: { registeredTourns } }, tournaments) => {
        const registeredTiles = {};
        registeredTourns.forEach(({ tournId, clubId }) => {
          const match = tournaments[Number(clubId)]?.find(
            (tournament) => tournId?.toString() === tournament.id
          );

          if (!registeredTiles[Number(clubId)]) {
            registeredTiles[Number(clubId)] = [];
          }

          if (match) {
            return (registeredTiles[Number(clubId)] = [...registeredTiles[Number(clubId)], match]);
          }
        });
        return registeredTiles;
      }
    );
  },
  uiLayout() {
    return slice((state) => state.uiLayout);
  }
});
